// src/services/locationApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

// Location interfaces
export interface Country {
  _id: string;
  name: string;
  code: string;
  geonameId: number;
}

export interface State {
  _id: string;
  name: string;
  code: string;
  countryId: string;
  geonameId: number;
}

export interface City {
  _id: string;
  name: string;
  stateId: string;
  geonameId: number;
}

export const locationApi = createApi({
  reducerPath: 'locationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}locations`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      return headers;
    },
  }),
  tagTypes: ['Countries', 'States', 'Cities'],
  endpoints: (builder) => ({
    // ============================================================================
    // LOCATION DATA ENDPOINTS
    // ============================================================================
    
    // Get all countries
    getCountries: builder.query<Country[], void>({
      query: () => '/countries',
      providesTags: ['Countries'],
      transformResponse: (response: Country[]) => {
        // Sort countries alphabetically by name
        return response.sort((a, b) => a.name.localeCompare(b.name));
      }
    }),
    
    // Get states by country
    getStates: builder.query<State[], string>({
      query: (countryId) => ({
        url: '/states',
        params: { countryId }
      }),
      // @ts-ignore
      providesTags: (result, error, countryId) => [
        { type: 'States', id: countryId }
      ],
      transformResponse: (response: State[]) => {
        // Sort states alphabetically by name
        return response.sort((a, b) => a.name.localeCompare(b.name));
      }
    }),
    
    // Get cities by state
    getCities: builder.query<City[], string>({
      query: (stateId) => ({
        url: '/cities',
        params: { stateId }
      }),
      // @ts-ignore
      providesTags: (result, error, stateId) => [
        { type: 'Cities', id: stateId }
      ],
      transformResponse: (response: City[]) => {
        // Sort cities alphabetically by name
        return response.sort((a, b) => a.name.localeCompare(b.name));
      }
    }),
    
    // Get random cities for testing
    getRandomCities: builder.query<City[], void>({
      query: () => '/cities/random',
      keepUnusedDataFor: 0 // Don't cache random results
    }),
    
    // Sync location data (admin only)
    syncAllLocations: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/sync',
        method: 'POST'
      }),
      invalidatesTags: ['Countries', 'States', 'Cities']
    }),
    
    // Sync specific country locations
    syncCountryLocations: builder.mutation<any, string>({
      query: (countryId) => ({
        url: '/sync/country',
        method: 'POST',
        params: { countryId }
      }),
      // @ts-ignore
      invalidatesTags: (result, error, countryId) => [
        { type: 'States', id: countryId },
        { type: 'Cities', id: 'LIST' }
      ]
    })
  })
});

export const {
  // Basic location hooks
  useGetCountriesQuery,
  useGetStatesQuery,
  useGetCitiesQuery,
  useGetRandomCitiesQuery,
  
  // Sync hooks
  useSyncAllLocationsMutation,
  useSyncCountryLocationsMutation,
  
  // Manual hooks for conditional queries
  useLazyGetStatesQuery,
  useLazyGetCitiesQuery
} = locationApi;